import { useRef, useState } from 'react';
import { CloudUpload, Loader2, MapPin, Thermometer, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

import { addPhotoToWaypoint } from '@/services/photo-service';
import { InlineErrorBanner } from '@/components/common/InlineErrorBanner';
import { usePhotoSrc } from '@/hooks/use-photo-src';
import type { PhotoRecord } from '@/types/photo';
import type { CameraMetadata } from '@/types/waypoint';
import type { ExifData } from '@/services/exif-service';

export interface CameraPhotoUploadProps {
  waypointId: string;
  userId: string;
  cameraMetadata: CameraMetadata;
  onPhotosAdded: (photos: PhotoRecord[]) => void;
  onMetadataUpdate: (updates: Partial<CameraMetadata>) => void;
}

interface UploadResult {
  photo: PhotoRecord;
  exif: ExifData;
  filename: string;
}

interface MetadataBannerProps {
  suggestedModel: string;
  currentModel: string | undefined;
  onAccept: () => void;
  onDismiss: () => void;
}

function MetadataBanner({ suggestedModel, currentModel, onAccept, onDismiss }: MetadataBannerProps) {
  const isEmpty = !currentModel;
  const differs = currentModel && currentModel !== suggestedModel;
  if (!isEmpty && !differs) return null;

  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg bg-[var(--color-elevated)] border border-[var(--color-border-subtle)] text-body-xs" data-testid="metadata-update-banner">
      <p className="text-[var(--color-text-secondary)]">
        Photos taken with <span className="font-medium text-[var(--color-text-primary)]">{suggestedModel}</span>.
        {isEmpty ? ' Update camera model?' : ` Replace "${currentModel}"?`}
      </p>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          onClick={onAccept}
          className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] font-medium transition-colors"
          data-testid="metadata-accept-button"
        >
          Update
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[var(--color-text-placeholder)] hover:text-[var(--color-text-muted)] transition-colors"
          data-testid="metadata-dismiss-button"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function ExifSummaryCard({ result }: { result: UploadResult }) {
  const { exif, filename } = result;
  // usePhotoSrc manages blob URL lifecycle (create + revoke on unmount) — RULE-025
  const { src } = usePhotoSrc(result.photo.id);

  return (
    <div className="flex gap-3 p-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)]" data-testid="exif-summary-card">
      {src && (
        <img
          src={src}
          alt={filename}
          className="w-14 h-14 object-cover rounded-md flex-shrink-0"
        />
      )}
      <div className="flex flex-col gap-0.5 min-w-0">
        <p className="text-body-xs text-[var(--color-text-primary)] font-medium truncate">{filename}</p>
        {exif.dateTimeOriginal && (
          <p className="text-body-xs text-[var(--color-text-muted)]">
            {format(exif.dateTimeOriginal, 'MMM d, yyyy h:mm a')}
          </p>
        )}
        {(exif.make || exif.model) && (
          <p className="text-body-xs text-[var(--color-text-placeholder)]">
            {[exif.make, exif.model].filter(Boolean).join(' ')}
          </p>
        )}
        <div className="flex gap-3 mt-0.5">
          {exif.gpsLatitude !== null && exif.gpsLongitude !== null && (
            <span className="flex items-center gap-1 text-body-xs text-[var(--color-text-placeholder)]">
              <MapPin size={10} />
              {exif.gpsLatitude.toFixed(4)}, {exif.gpsLongitude.toFixed(4)}
            </span>
          )}
          {exif.temperature !== null && (
            <span className="flex items-center gap-1 text-body-xs text-[var(--color-text-placeholder)]">
              <Thermometer size={10} />
              {exif.temperature}°F
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function CameraPhotoUpload({
  waypointId,
  userId,
  cameraMetadata,
  onPhotosAdded,
  onMetadataUpdate,
}: CameraPhotoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedResults, setUploadedResults] = useState<UploadResult[]>([]);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  // Track if user dismissed the metadata banner
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Derive suggested model from the most recently uploaded EXIF
  const suggestedModel = uploadedResults
    .map((r) => r.exif.model)
    .find((m): m is string => !!m) ?? null;

  const showBanner =
    !bannerDismissed &&
    suggestedModel !== null &&
    suggestedModel !== cameraMetadata.cameraModel;

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    setIsUploading(true);
    setProgress({ current: 0, total: files.length });
    setFailedFiles([]);

    const results: UploadResult[] = [];
    const failed: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress({ current: i + 1, total: files.length });
      try {
        const record = await addPhotoToWaypoint(waypointId, userId, file);
        results.push({
          photo: record,
          // SAFETY: exifData is always set by addPhotoToWaypoint
          exif: record.exifData!,
          filename: file.name,
        });
      } catch (err) {
        console.error(`[CameraPhotoUpload] Failed to upload ${file.name}:`, err);
        failed.push(file.name);
      }
    }

    setUploadedResults((prev) => [...prev, ...results]);
    if (failed.length > 0) setFailedFiles(failed);
    setProgress(null);
    setIsUploading(false);
    setBannerDismissed(false);

    if (results.length > 0) {
      onPhotosAdded(results.map((r) => r.photo));
    }

    // Reset input so the same files can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleAcceptMetadata() {
    if (suggestedModel) {
      onMetadataUpdate({ cameraModel: suggestedModel });
    }
    setBannerDismissed(true);
  }

  const hasResults = uploadedResults.length > 0;

  return (
    <div className="flex flex-col gap-3" data-testid="camera-photo-upload">
      {/* Upload button */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-body-sm font-medium transition-colors ${
          isUploading
            ? 'opacity-40 cursor-not-allowed bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-placeholder)]'
            : 'bg-[var(--color-surface)] border-[var(--color-border-subtle)] text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] hover:border-[var(--color-border)]'
        }`}
        data-testid="upload-button"
      >
        {isUploading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            {progress ? `Uploading ${progress.current} / ${progress.total}…` : 'Processing…'}
          </>
        ) : (
          <>
            <CloudUpload size={16} />
            Upload Trail Cam Photos
          </>
        )}
      </button>

      {/* Metadata update banner */}
      {showBanner && (
        <MetadataBanner
          suggestedModel={suggestedModel}
          currentModel={cameraMetadata.cameraModel}
          onAccept={handleAcceptMetadata}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Failed file errors */}
      {failedFiles.length > 0 && (
        <InlineErrorBanner
          message={`Failed to upload: ${failedFiles.join(', ')}`}
          data-testid="upload-errors"
        />
      )}

      {/* EXIF summary cards */}
      {hasResults && (
        <div className="flex flex-col gap-2">
          <p className="flex items-center gap-1.5 text-body-xs text-[var(--color-text-placeholder)]">
            <CheckCircle size={12} className="text-green-400" />
            {uploadedResults.length} photo{uploadedResults.length !== 1 ? 's' : ''} uploaded
          </p>
          {uploadedResults.map((result) => (
            <ExifSummaryCard key={result.photo.id} result={result} />
          ))}
        </div>
      )}

      {/* Off-screen file input — opacity-0 instead of display:none for reliable .click() on mobile */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="absolute opacity-0 pointer-events-none w-px h-px"
        onChange={(e) => void handleFiles(e.target.files)}
        data-testid="camera-file-input"
      />
    </div>
  );
}
